// src/components/Conversions.jsx
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ConfirmDialog from './ConfirmDialog';
import { useLockBodyScroll } from '../hooks/useLockBodyScroll';
import { today, PRESETS, formatNY } from '../utils/dateRange';

function Conversions() {
  const [conversions, setConversions] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { start: todayStart, end: todayEnd } = today();
  const [filters, setFilters] = useState({
    campaign_id: '',
    advertiser_id: '',
    status: '',
    start_date: todayStart,
    end_date: todayEnd,
    limit: 100
  });

  const applyPreset = ({ start, end }) =>
    setFilters(f => ({ ...f, start_date: start, end_date: end }));
  const [stats, setStats] = useState(null);
  const [toast, setToast] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null });
  const [showFilters, setShowFilters] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [addStep, setAddStep] = useState('input'); // 'input' | 'confirm'
  const [lookupClickId, setLookupClickId] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [manualForm, setManualForm] = useState({ revenue: '', payout: '', status: 'valid' });
  const debounceRef = useRef(null);

  useEffect(() => {
    fetchCampaigns();
    fetchAdvertisers();
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchConversions();
      fetchStats();
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [filters]);

  const fetchConversions = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/conversions', { params: filters });
      setConversions(response.data.conversions);
    } catch (error) {
      console.error('Failed to fetch conversions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/conversions/stats/summary', { 
        params: {
          start_date: filters.start_date,
          end_date: filters.end_date,
          campaign_id: filters.campaign_id,
          advertiser_id: filters.advertiser_id
        }
      });
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get('/campaigns');
      setCampaigns(response.data.campaigns);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    }
  };

  const fetchAdvertisers = async () => {
    try {
      const response = await axios.get('/advertisers');
      setAdvertisers(response.data.advertisers);
    } catch (error) {
      console.error('Failed to fetch advertisers:', error);
    }
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const handleStatusChange = async (conversionId, newStatus) => {
    try {
      await axios.put(`/conversions/${conversionId}`, { status: newStatus });
      fetchConversions();
      fetchStats();
    } catch (error) {
      showToast('Failed to update conversion status');
    }
  };

  const handleDelete = (conversionId) => setConfirmDialog({ open: true, id: conversionId });
  const performDelete = async () => {
    try {
      await axios.delete(`/conversions/${confirmDialog.id}`);
      fetchConversions();
      fetchStats();
    } catch { showToast('Failed to delete conversion'); }
    setConfirmDialog({ open: false, id: null });
  };

  const getStatusColor = (status) => {
    const colors = {
      valid: 'bg-green-100 text-green-800',
      invalid: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
      reversed: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  useLockBodyScroll(addModal);

  const openAddModal = () => {
    setLookupClickId('');
    setLookupResult(null);
    setAddStep('input');
    setAddModal(true);
  };

  const closeAddModal = () => {
    setAddModal(false);
    setLookupResult(null);
    setLookupClickId('');
    setAddStep('input');
  };

  const handleLookup = async () => {
    const trimmed = lookupClickId.trim();
    if (!trimmed) return;
    setLookupLoading(true);
    try {
      const res = await axios.get('/conversions/lookup', { params: { click_id: trimmed } });
      const { click, existing_conversion } = res.data;
      setLookupResult(res.data);
      setManualForm({
        revenue: parseFloat(click.default_payout || 0).toFixed(2),
        payout: parseFloat(click.default_payout || 0).toFixed(2),
        status: 'valid'
      });
      setAddStep('confirm');
    } catch (err) {
      showToast(err.response?.data?.error || 'Click ID not found');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleManualCreate = async () => {
    try {
      await axios.post('/conversions/manual', {
        click_id: lookupClickId.trim(),
        revenue: manualForm.revenue,
        payout: manualForm.payout,
        status: manualForm.status
      });
      closeAddModal();
      fetchConversions();
      fetchStats();
      showToast('Conversion created successfully');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to create conversion');
    }
  };

  const todayStr = today().start;
  const hasActiveFilters =
    filters.campaign_id || filters.advertiser_id || filters.status ||
    Number(filters.limit) !== 100 ||
    filters.start_date !== todayStr || filters.end_date !== todayStr;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center gap-3">
        <h1 className="text-3xl font-bold text-gray-900">Conversions</h1>
        <div className="flex gap-2 flex-wrap justify-end">
          <button
            type="button"
            onClick={openAddModal}
            className="inline-flex items-center gap-2 px-4 py-2 min-h-[44px] text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 active:bg-blue-800 shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="sm:hidden">Add</span>
            <span className="hidden sm:inline">Add Conversion</span>
          </button>
          <button
            type="button"
            onClick={() => { fetchConversions(); fetchStats(); }}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 min-h-[44px] text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 active:bg-gray-100 shadow-sm disabled:opacity-50"
            title="Refresh data"
          >
            <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <button
            type="button"
            onClick={() => setShowFilters(v => !v)}
            className="inline-flex items-center gap-2 px-4 py-2 min-h-[44px] text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 active:bg-gray-100 shadow-sm"
            aria-expanded={showFilters}
            aria-controls="filter-panel"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            {showFilters ? 'Hide Filters' : 'Show Filters'}
            {hasActiveFilters && (
              <span className="inline-block w-2 h-2 bg-blue-500 rounded-full" aria-label="Filters applied" />
            )}
          </button>
        </div>
      </div>

      {/* Stats Cards - always visible */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-500">Total Conversions</div>
            <div className="text-2xl font-bold text-gray-900">{stats.total_conversions || 0}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-500">Valid Conversions</div>
            <div className="text-2xl font-bold text-green-600">{stats.valid_conversions || 0}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-500">Total Payout</div>
            <div className="text-2xl font-bold text-blue-600">
              ${parseFloat(stats.total_payout || 0).toFixed(2)}
            </div>
          </div>
        </div>
      )}

      {/* Filters — collapsible */}
      {showFilters && (
        <div id="filter-panel" className="bg-white p-6 rounded-lg shadow">
          <div className="flex flex-wrap gap-2 mb-4">
            {PRESETS.map(p => (
              <button
                key={p.key}
                type="button"
                onClick={() => applyPreset(p.get())}
                className="px-3 py-2 min-h-[44px] text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 active:bg-gray-100"
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Campaign</label>
              <select
                value={filters.campaign_id}
                onChange={(e) => setFilters({ ...filters, campaign_id: e.target.value })}
                className="block w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="">All Campaigns</option>
                {campaigns.map(campaign => (
                  <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Advertiser</label>
              <select
                value={filters.advertiser_id}
                onChange={(e) => setFilters({ ...filters, advertiser_id: e.target.value })}
                className="block w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="">All Advertisers</option>
                {advertisers.map(advertiser => (
                  <option key={advertiser.id} value={advertiser.id}>{advertiser.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="block w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="">All Status</option>
                <option value="valid">Valid</option>
                <option value="invalid">Invalid</option>
                <option value="pending">Pending</option>
                <option value="reversed">Reversed</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                className="block w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                className="block w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Limit</label>
              <select
                value={filters.limit}
                onChange={(e) => setFilters({ ...filters, limit: e.target.value })}
                className="block w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="250">250</option>
                <option value="500">500</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Conversions Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : conversions.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No conversions found matching your filters.</p>
          </div>
        ) : (
          <>
            {/* Mobile: card list */}
            <ul className="sm:hidden divide-y divide-gray-200">
              {conversions.map((conversion) => (
                <li key={conversion.id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 truncate">{conversion.campaign_name}</div>
                      <div className="text-sm text-gray-500 truncate">{conversion.advertiser_name} · {conversion.vps_name}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{formatNY(conversion.created_at)}</div>
                    </div>
                    <div className="shrink-0 text-sm font-semibold text-green-600">
                      ${parseFloat(conversion.payout).toFixed(2)}
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <select
                      value={conversion.status}
                      onChange={(e) => handleStatusChange(conversion.id, e.target.value)}
                      className="flex-1 border border-gray-300 rounded px-2 py-2 text-sm bg-white text-gray-700"
                    >
                      <option value="valid">Valid</option>
                      <option value="invalid">Invalid</option>
                      <option value="pending">Pending</option>
                      <option value="reversed">Reversed</option>
                    </select>
                    <button
                      onClick={() => handleDelete(conversion.id)}
                      className="min-h-[44px] px-3 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-md active:bg-red-100"
                    >
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            {/* Desktop: existing table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Advertiser</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">VPS</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Click ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payout</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {conversions.map((conversion) => (
                    <tr key={conversion.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {conversion.campaign_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {conversion.advertiser_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {conversion.vps_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500">
                        {conversion.click_id?.substring(0, 12)}...
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                        ${parseFloat(conversion.payout).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={conversion.status}
                          onChange={(e) => handleStatusChange(conversion.id, e.target.value)}
                          className="border border-gray-300 rounded px-2 py-2 text-base bg-white text-gray-700"
                        >
                          <option value="valid">Valid</option>
                          <option value="invalid">Invalid</option>
                          <option value="pending">Pending</option>
                          <option value="reversed">Reversed</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatNY(conversion.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handleDelete(conversion.id)}
                          className="text-red-600 hover:text-red-900 active:text-red-700"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {toast && (
        <div className="fixed bottom-4 right-4 bg-gray-800 text-white px-4 py-2 rounded-md shadow-lg text-sm">
          {toast}
        </div>
      )}

      <ConfirmDialog
        open={confirmDialog.open}
        title="Delete conversion?"
        message="This cannot be undone."
        confirmLabel="Delete"
        danger
        onConfirm={performDelete}
        onCancel={() => setConfirmDialog({ open: false, id: null })}
      />

      {/* Add Conversion Modal */}
      {addModal && (
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50"
          onClick={closeAddModal}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90dvh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal header — shrink-0 so keyboard open can't collapse it */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
              <h2 className="text-lg font-semibold text-gray-900">
                {addStep === 'input' ? 'Add Missing Conversion' : 'Review & Confirm'}
              </h2>
              <button
                type="button"
                onClick={closeAddModal}
                className="text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Step 1 — enter click_id */}
            {addStep === 'input' && (
              <>
                {/* Scrollable body — flex-1 so footer stays pinned */}
                <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-5 flex flex-col gap-4">
                  <p className="text-sm text-gray-600">
                    Enter the Click ID from the advertiser's postback to look up the original click and create a conversion record.
                  </p>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Click ID</label>
                    <input
                      type="text"
                      inputMode="text"
                      autoCapitalize="none"
                      autoCorrect="off"
                      autoComplete="off"
                      spellCheck="false"
                      value={lookupClickId}
                      onChange={e => setLookupClickId(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleLookup()}
                      placeholder="e.g. d10da140b50f73ab95aac3a578594fe6"
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 font-mono"
                    />
                  </div>
                </div>
                {/* Footer — outside scroll container, always visible */}
                <div className="shrink-0 flex justify-end gap-3 px-6 py-4 bg-white border-t border-gray-100">
                  <button
                    type="button"
                    onClick={closeAddModal}
                    className="min-h-[44px] px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 active:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleLookup}
                    disabled={!lookupClickId.trim() || lookupLoading}
                    className="min-h-[44px] px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50"
                  >
                    {lookupLoading ? 'Looking up…' : 'Look Up'}
                  </button>
                </div>
              </>
            )}

            {/* Step 2 — review click details and set conversion values */}
            {addStep === 'confirm' && lookupResult && (
              <>
                {/* Scrollable body */}
                <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-5 flex flex-col gap-4">

                  {/* Existing conversion warning */}
                  {lookupResult.existing_conversion && (
                    <div className="flex gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
                      <svg className="w-5 h-5 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      </svg>
                      <span>
                        A <strong>{lookupResult.existing_conversion.status}</strong> conversion already exists for this click
                        (created {formatNY(lookupResult.existing_conversion.created_at)}). Creating another will be blocked by the database unique constraint.
                      </span>
                    </div>
                  )}

                  {/* Click details */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Click Details</h3>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      <dt className="text-gray-500">Click ID</dt>
                      <dd className="font-mono text-gray-900 truncate">{lookupResult.click.click_id}</dd>
                      <dt className="text-gray-500">Campaign</dt>
                      <dd className="text-gray-900">{lookupResult.click.campaign_name}</dd>
                      <dt className="text-gray-500">Advertiser</dt>
                      <dd className="text-gray-900">{lookupResult.click.advertiser_name}</dd>
                      <dt className="text-gray-500">VPS</dt>
                      <dd className="text-gray-900">{lookupResult.click.vps_name}</dd>
                      <dt className="text-gray-500">IP</dt>
                      <dd className="text-gray-900">{lookupResult.click.ip_address}</dd>
                      <dt className="text-gray-500">Country</dt>
                      <dd className="text-gray-900">{lookupResult.click.country || '—'}</dd>
                      <dt className="text-gray-500">Device</dt>
                      <dd className="text-gray-900">{lookupResult.click.device || '—'}</dd>
                      <dt className="text-gray-500">Clicked at</dt>
                      <dd className="text-gray-900">{formatNY(lookupResult.click.created_at)}</dd>
                    </dl>
                  </div>

                  {/* Conversion values — stack on mobile, 3 cols on sm+ */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Conversion Values</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Revenue ($)</label>
                        <input
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          min="0"
                          value={manualForm.revenue}
                          onChange={e => setManualForm(f => ({ ...f, revenue: e.target.value }))}
                          className="block w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Payout ($)</label>
                        <input
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          min="0"
                          value={manualForm.payout}
                          onChange={e => setManualForm(f => ({ ...f, payout: e.target.value }))}
                          className="block w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Status</label>
                        <select
                          value={manualForm.status}
                          onChange={e => setManualForm(f => ({ ...f, status: e.target.value }))}
                          className="block w-full border border-gray-300 rounded-md px-3 py-2"
                        >
                          <option value="valid">Valid</option>
                          <option value="pending">Pending</option>
                          <option value="invalid">Invalid</option>
                          <option value="reversed">Reversed</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer — outside scroll container, always visible */}
                <div className="shrink-0 flex justify-between items-center gap-3 px-6 py-4 bg-white border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setAddStep('input')}
                    className="min-h-[44px] px-4 py-2 text-sm text-gray-600 hover:text-gray-900 active:text-gray-700"
                  >
                    ← Back
                  </button>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={closeAddModal}
                      className="min-h-[44px] px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 active:bg-gray-100"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleManualCreate}
                      className="min-h-[44px] px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 active:bg-blue-800"
                    >
                      Create Conversion
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Conversions;